import React from 'react';
import PropTypes from 'prop-types';

import { isSuspended, isVisible } from '@atlas/core';
import { marketsUsesSpecifierByName } from '@atlas/display-layouts';

import { NON_PARAMETRIZED_MARKETS_WITH_NAME } from 'common/constants';
import eventShape from 'common/PropTypes/eventShape';
import marketShape from 'common/PropTypes/marketShape';
import marketTypeShape from 'common/PropTypes/marketTypeShape';

import SimpleOutcomeColumn from './SimpleOutcomeColumn';

const SimpleOutcomeComponent = props => {
  const { className, event, market = {}, marketType, suspended } = props;

  const parameterized = marketType.parameterized && marketType.id !== 346;
  const onlySpecifierName = marketsUsesSpecifierByName.includes(marketType.id);
  const marketWithName =
    parameterized || NON_PARAMETRIZED_MARKETS_WITH_NAME.includes(marketType.id);

  const orderedOutcomes = React.useMemo(() => {
    if (Object.keys(market).length === 0 || !market.outcomes) {
      return null;
    }

    return Object.values(market.outcomes)
      .filter(outcome => isVisible(outcome))
      .sort((a, b) => (a.orderPosition || 2) - (b.orderPosition || 2));
  }, [market]);

  const customName = React.useMemo(
    () => (shortName, specifiers) => {
      if (onlySpecifierName) {
        return specifiers;
      }

      if (marketWithName && !onlySpecifierName) {
        return specifiers ? `${shortName}/${specifiers}` : shortName;
      }

      return null;
    },
    [],
  );

  const marketSuspended = suspended || isSuspended(market);

  if (
    Object.keys(marketType).length === 0 ||
    Object.keys(market).length === 0 ||
    Object.keys(market).length === 0 ||
    orderedOutcomes.length === 0
  ) {
    const EmptyOutcome = () => (
      <div className="market-container absent">
        <span className="outcome primary" />
      </div>
    );

    return (
      <div className="market-row">
        {Array(3)
          .fill(EmptyOutcome)
          .map((EmptyOutcome, i) => (
            <EmptyOutcome key={`${i}_key`} />
          ))}
      </div>
    );
  }

  return (
    <div className="market-row">
      {orderedOutcomes?.map(outcome => {
        const { id, odds, shortName, specifiers } = outcome;

        if (!odds) {
          return null;
        }

        return (
          <SimpleOutcomeColumn
            className={className}
            customName={(marketWithName || onlySpecifierName) && customName(shortName, specifiers)}
            event={event}
            id={id}
            key={id}
            market={market}
            marketType={marketType}
            outcome={outcome}
            suspended={marketSuspended}
          />
        );
      })}
    </div>
  );
};

SimpleOutcomeComponent.propTypes = {
  event: eventShape.isRequired,
  market: marketShape,
  marketType: marketTypeShape,
  suspended: PropTypes.bool,
};

export default SimpleOutcomeComponent;
